import React, { useContext } from 'react'
import Auth0ProviderWithHistory from '../../Auth0ProviderWithHistory'
import {
  BrowserRouter,
  Switch,
  Route,
  Redirect,
  RouteProps,
  useLocation,
  useParams,
} from 'react-router-dom'
import { RouterContextProvider } from 'components/router/context/RouterContextProvider'
import { Campaigns } from 'pages/business/campaigns/Campaigns'
import { Creatives } from 'pages/business/creatives/Creatives'
import { Login } from 'pages/auth/login'
import { ListLeadCards } from 'pages/business/lead-cards/ListLeadCards'
import { EditLeadCard } from 'pages/business/lead-cards/EditLeadCard'
import { CreativeImportPage } from 'pages/business/creatives/creative-import/CreativeImportPage'
import { ListAudiences } from 'pages/business/audiences/list-audiences'
import { CreateLeadCard } from 'pages/business/lead-cards/CreateLeadCard'
import { DuplicateLeadCard } from 'pages/business/lead-cards/DuplicateLeadCard'
import { InvalidUser } from 'pages/auth/login/InvalidUser'
import { BusinessList } from 'pages/business/business-list/BusinessList'
import { Sprints } from 'pages/business/sprints/Sprints'
import { LocalCompetition } from 'pages/business/local-competition/LocalCompetition'
import { AIStudio } from 'pages/business/ai-studio/AIStudio'
import { ListSprintPackets } from 'pages/business/sprint-packets/ListSprintPackets'
import { EditSprintPacket } from 'pages/business/sprint-packets/EditSprintPacket'
import { PresentSprintPacket } from 'pages/business/sprint-packets/PresentSprintPacket'
import { ReviewSprintPacket } from 'pages/business/sprint-packets/ReviewSprintPacket'
import { sessionContext, useGetBusinessIfExists } from '@marketing-milk/frontend'
import { toast } from 'react-toastify'

type BusinessPageRequiredProps = {
  children: React.ReactElement
}

// only guards by if business has a FB page as of now
export function BusinessPageRequired({ children }: BusinessPageRequiredProps) {
  const { user } = useContext(sessionContext)
  const business = useGetBusinessIfExists()
  if (business?.fbPageID) {
    return children
  } else {
    if (business) {
      toast.error(
        `You cannot use Ad Builder without a Facebook Page configured for ${business.businessName}`
      )
    }
    // Where this bounces to has to depend on whether there is a session, and
    // sending both cases to the same place is an infinite loop either way.
    // Signed out, the business list is empty for every URL, so the business
    // can't be judged yet — `/login` resolves that and returns to the URL that
    // was asked for. Signed in, the business is genuinely unusable here, and
    // `/login` would send the user straight back to this route.
    return <Redirect to={user ? '/dashboard' : '/login'} />
  }
}

type GuardedRouteProps = {
  children: React.ReactElement
} & RouteProps

// only guards by if business has a FB page as of now
function GuardedRoute({ children, ...routeProps }: GuardedRouteProps) {
  return (
    <Route {...routeProps}>
      <BusinessPageRequired>{children}</BusinessPageRequired>
    </Route>
  )
}

/**
 * An ad used to open on a page of its own; it now opens in the editor over the
 * page that lists it, as `?ad=`. Old links — bookmarks, Slack, anything that
 * built the path — land there instead, keeping whatever query they carried (a
 * sprint's own `sprintId`/`tab`).
 */
export function OpenAdRedirect({ list }: { list: 'creatives' | 'sprints' }) {
  const { businessID, creativeID } = useParams<{ businessID: string; creativeID: string }>()
  const params = new URLSearchParams(useLocation().search)
  params.set('ad', creativeID)
  // The editor only mounts over the creatives tab, and old links carry whichever
  // tab they were opened from.
  if (list === 'sprints') params.set('tab', 'creatives')
  return <Redirect to={`/${businessID}/${list}?${params.toString()}`} />
}

export function Router() {
  return (
    <BrowserRouter basename="/builder">
      <RouterContextProvider>
        <Auth0ProviderWithHistory>
          <Switch>
            {/* Campaigns */}
            <GuardedRoute path="/:businessID/campaigns">
              <Campaigns />
            </GuardedRoute>

            {/* Audiences */}
            <GuardedRoute path="/:businessID/audiences">
              <ListAudiences />
            </GuardedRoute>

            {/* Creatives */}
            <GuardedRoute exact path="/:businessID/creatives/import/:creativeImportID?">
              <CreativeImportPage />
            </GuardedRoute>
            <Route path="/:businessID/creatives/:creativeID">
              <OpenAdRedirect list="creatives" />
            </Route>
            <Route path="/:businessID/sprints/:creativeID">
              <OpenAdRedirect list="sprints" />
            </Route>
            <GuardedRoute path="/:businessID/creatives">
              <Creatives />
            </GuardedRoute>

            {/* Sprints */}
            <GuardedRoute path="/:businessID/sprints">
              <Sprints />
            </GuardedRoute>

            {/* Local Competition */}
            <GuardedRoute path="/:businessID/competition">
              <LocalCompetition />
            </GuardedRoute>

            {/* AI Studio */}
            <GuardedRoute path="/:businessID/ai-studio">
              <AIStudio />
            </GuardedRoute>

            {/* Lead Cards */}
            <GuardedRoute exact path="/:businessID/lead-cards/duplicate/:leadCardID">
              <DuplicateLeadCard />
            </GuardedRoute>
            <GuardedRoute exact path="/:businessID/lead-cards/create">
              <CreateLeadCard />
            </GuardedRoute>
            <GuardedRoute exact path="/:businessID/lead-cards/:leadCardID">
              <EditLeadCard />
            </GuardedRoute>
            <GuardedRoute exact path="/:businessID/lead-cards">
              <ListLeadCards />
            </GuardedRoute>

            {/* Sprint Packets */}
            <GuardedRoute exact path="/:businessID/sprint-packets/:packetID/edit">
              <EditSprintPacket />
            </GuardedRoute>
            <GuardedRoute exact path="/:businessID/sprint-packets/:packetID/present">
              <PresentSprintPacket />
            </GuardedRoute>
            <GuardedRoute exact path="/:businessID/sprint-packets/:packetID/review">
              <ReviewSprintPacket />
            </GuardedRoute>
            <GuardedRoute exact path="/:businessID/sprint-packets">
              <ListSprintPackets />
            </GuardedRoute>

            {/* Business List */}
            <Route path="/dashboard">
              <BusinessList />
            </Route>

            {/* Auth */}
            <Route path="/login">
              <Login />
            </Route>
            <Route path="/invalid_user">
              <InvalidUser />
            </Route>
            <Route>
              <Redirect to="/login" />
            </Route>
          </Switch>
        </Auth0ProviderWithHistory>
      </RouterContextProvider>
    </BrowserRouter>
  )
}
